import { MKTU_CLASS_CATEGORY } from '@/models/Mktu'
import { apiService } from '@/services/Api'
import { MktuClass } from '@/services/Api/generated'
import { exportMktuListToFile } from '@/shared/Mktu/utils'
import { mainStore } from '@/domains/Account/store'
import { Loader } from '@/helpers/Loader'
import { TYPE_MESSAGE_POPUP } from '@/store/Message'
import { MktuClassStore } from '@/store/Mktu/MktuClass'
import { MktuClassesList } from '@/store/Mktu/MktuClassesList'
import { SearchMktuClassStore } from '@/store/Mktu/SearchMktuClass'
import { mimeToExtensionsString } from '@/utils/file'
import { getFileSizeWithPrefix } from '@/utils/fileSizes'
import { makeAutoObservable } from 'mobx'

export class MktuStore {
  searchStore = new SearchMktuClassStore(this.mktuClassesList)
  idEditableClass = null

  get classes(): MktuClassStore[] {
    return this.mktuClassesList.classes
  }

  loaderExport = new Loader(async () => {
    await exportMktuListToFile(this.selectedMktuClasses)
  }, {})

  loaderImport = new Loader(async (file: File) => {
    const maxSize = mainStore.serverOptions.options.ALLOWED_MKTU_MAX_SIZE

    const supportedFormat =
      mainStore.serverOptions.options.ALLOWED_MKTU_MIME_TYPES

    // Проверка размера
    if (file.size >= maxSize) {
      const maxFileSizeWithPrefix = getFileSizeWithPrefix(maxSize)
      const message = `Размер файла не должен превышать ${maxFileSizeWithPrefix}`

      mainStore.message.show({
        text: message,
        type: TYPE_MESSAGE_POPUP.ERROR,
      })
      return
    }

    // Проверка типа файла
    if (!supportedFormat.includes(file.type)) {
      const formatText = mimeToExtensionsString(supportedFormat)
      const message = 'Поддерживаемые типы файлов: ' + formatText

      mainStore.message.show({
        text: message,
        type: TYPE_MESSAGE_POPUP.ERROR,
      })
      return
    }

    const response = await apiService.mktu._import({
      file,
    })
    if (response.status) {
      for (const mktuClassData of response.data) {
        const mktuClass = this.classes.find(
          (c) => c.number === mktuClassData.classNumber
        )
        if (mktuClass != null) {
          mktuClass.setSelectedWordsNames(mktuClassData.selectedWords)
        }
      }
    }
  }, {})

  constructor(public mktuClassesList: MktuClassesList) {
    makeAutoObservable(this, {}, { autoBind: true })

    mktuClassesList.loader.request()
  }

  get editableClass() {
    return this.classes.find((i) => i.uuid === this.idEditableClass)
  }

  setIdEditableClass(uuid: MktuClass['uuid']) {
    this.idEditableClass = uuid
  }

  get mapClassesByType() {
    return {
      [MKTU_CLASS_CATEGORY.PRODUCT]: this.classes.filter(
        (c) => c.type === MKTU_CLASS_CATEGORY.PRODUCT
      ),
      [MKTU_CLASS_CATEGORY.SERVICE]: this.classes.filter(
        (c) => c.type === MKTU_CLASS_CATEGORY.SERVICE
      ),
    }
  }

  joinFoundClasses() {
    this.searchStore.selectedFoundClasses.forEach((foundMktuClass) => {
      const mktuClass = this.classes.find((c) => c.uuid === foundMktuClass.uuid)
      if (mktuClass != null) {
        const joinSelectedWordNames = foundMktuClass.selectedWordsNames.reduce(
          (wordsNames, wordName) => {
            if (!wordsNames.includes(wordName)) {
              return [...wordsNames, wordName]
            } else {
              return wordsNames
            }
          },
          [...mktuClass.selectedWordsNames]
        )

        if (!mktuClass.wordsLoaded) {
          mktuClass.setWords(foundMktuClass.words)
        }

        mktuClass.setSelectedWordsNames(joinSelectedWordNames)
      }
    })

    // reset words
    this.searchStore.resetSelectedWords()
  }

  get selectedMktuClasses(): MktuClassStore[] {
    return this.classes.filter(
      (mktuClass) => mktuClass.selectedWordsNames.length !== 0
    )
  }

  get isValid() {
    return this.selectedMktuClasses.length !== 0
  }

  get preparedData(): { text: string; classNum: number }[] {
    return this.selectedMktuClasses.map((mktuClass) => ({
      classNum: mktuClass.number,
      text: mktuClass.selectedWordsNames.join('; '),
    }))
  }

  resetSelectedMktuClasses() {
    this.selectedMktuClasses.forEach((c) => {
      c.setSelectedWordsNames([])
    })
  }
}
